import { cn } from "@perceptron/utils/cn";

import { getLinkHost } from "@demo/libs/tool-calls/favicon";
import type { ToolCallLinkEntry } from "@demo/server/api/contracts/toolCall";
import { strings } from "@demo/utils/strings";

import Favicon from "./Favicon";
import { rowEntrance } from "./rowEntrance";
import { ShimmerText } from "./ShimmerText";
import ToolCallResultsBody, { describeResultCount } from "./ToolCallResultsBody";

export interface SearchResultsListProps {
  /** The query, shown in full above the results. */
  query?: string;
  results: ToolCallLinkEntry[];
  /** Shimmers each row while the search is still running. */
  isStreaming: boolean;
}

/** The expanded body of a web search: one row per result with its favicon and host. */
function SearchResultsList({ query, results, isStreaming }: SearchResultsListProps) {
  return (
    <ToolCallResultsBody
      isStreaming={isStreaming}
      query={query}
      status={describeResultCount(results.length)}
      statusTestId="search-results-count"
    >
      <ul className="flex w-full flex-col gap-2">
        {results.map(({ index, link }) => {
          const host = getLinkHost(link);
          const label = host ?? link.title ?? strings.toolCall.untitled;
          const text = (
            // Rows shimmer in the primary colour while the search runs and settle to secondary once done.
            <ShimmerText
              className="truncate label-2"
              data-testid="search-result-label"
              highlightOnHover={!!link.url}
              isStreaming={isStreaming}
              settledTone="secondary"
            >
              {label}
            </ShimmerText>
          );
          const entrance = rowEntrance(index, isStreaming);
          return (
            <li
              className={cn("flex w-full items-center gap-2", entrance.className)}
              data-index={index}
              data-testid="search-result-row"
              key={index}
              style={entrance.style}
            >
              <Favicon alt={label} host={host} key={host} url={link.url} />
              {link.url ? (
                <a
                  className="group min-w-px truncate"
                  href={link.url}
                  rel="noreferrer noopener"
                  target="_blank"
                  title={link.title ?? link.url}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </li>
          );
        })}
      </ul>
    </ToolCallResultsBody>
  );
}

export default SearchResultsList;
